import { memo, useCallback, useMemo, useRef, ReactElement } from 'react';
import { EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { Button, Flex, Space, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { Table, TableComponent, TableProps } from '@ohpen/enso-ui';
import { ActionEntityType, ProcurationFeeRuleResponse } from '@pmp/api';
import { Guard } from '@ohpen/react-authz';
import { useExportTemplatesStore } from '@pmp/stores';
import { ListProps } from '@pmp/types';
import { lockActionTitle } from '@pmp/utils';

import { procurationFeeRuleColumns } from '../tableConfig';

const entityType = ActionEntityType.PROCURATION_FEE_RULES;

export const ProcurationFeeRulesList = memo(
  ({ data, isFetching, valueLists, onCreate, onView, onEdit, onDelete }: ListProps<ProcurationFeeRuleResponse>): ReactElement => {
    const tableRef = useRef<TableComponent | null>(null);
    const { isLoadingExportTemplates, getExportTemplates } = useExportTemplatesStore();
    const enableExports = false; // useFeatureToggle(['enableExports']);
    const { t } = useTranslation();

    const getTemplates = useCallback(() => getExportTemplates(ActionEntityType.PROCURATION_FEE_RULES), [getExportTemplates]);

    const getRowActions: (record: ProcurationFeeRuleResponse) => ReactElement[] = useCallback(
      record => {
        const rowActions = [
          <Tooltip key="view" title={t('global.buttons.view')} placement="top">
            <Button type="text" aria-label="View Procuration Fee" icon={<EyeOutlined />} onClick={() => onView(record)} />
          </Tooltip>,
          <Guard key="edit" permissions={['procurationFees:update']} flags={['EnableProcurationFeeRulesCrud']}>
            <Tooltip title={lockActionTitle('edit', record.lockedByActionId)} placement="top">
              <Button
                type="text"
                aria-label="Edit Procuration Fee"
                icon={<EditOutlined key="edit" />}
                onClick={() => onEdit(record)}
                disabled={!!record.lockedByActionId}
              />
            </Tooltip>
          </Guard>,
          <Guard key="delete" permissions={['procurationFees:delete']} flags={['EnableProcurationFeeRulesCrud']}>
            <Tooltip title={lockActionTitle('delete', record.lockedByActionId)} placement="top">
              <Button
                type="text"
                aria-label="Delete Procuration Fee"
                icon={<DeleteOutlined key="delete" />}
                onClick={() => onDelete(record)}
                disabled={!!record.lockedByActionId}
              />
            </Tooltip>
          </Guard>,
        ];
        return rowActions;
      },
      [onDelete, onEdit, onView, t],
    );

    const tableProps: TableProps<ActionEntityType, ProcurationFeeRuleResponse, unknown, unknown, ProcurationFeeRuleResponse> = useMemo(
      () => ({
        id: entityType,
        itemsLabel: t('lender.procurationFees.labels.procurationFee'),
        ref: tableRef,
        dataSource: data ?? [],
        columns: procurationFeeRuleColumns,
        exportColumns: procurationFeeRuleColumns,
        rowKey: 'id',
        pagination: true,
        scroll: { x: 'max-content', y: '100%' },
        valueLists,
        loading: isFetching,
        getRowActions,
        showViews: false,
        // tableViews,
        // saveTableView,
        // deleteTableView,
      }),
      [t, data, valueLists, isFetching, getRowActions],
    );

    return (
      <Table {...tableProps}>
        <Table.ActionsBar>
          <Flex justify="space-between">
            <Space size="middle">
              <Table.GlobalSearch />
            </Space>
            <Space size="middle">
              <Table.ResetFiltersButton />
              <Table.SelectColumnsButton />
              {enableExports && (
                <Table.ExportButton
                  fileName={`Procuration Fees ${dayjs(new Date()).format('YYYY-MM-DD')}`}
                  sheetName="Procuration Fees"
                  disabled={isFetching}
                  loadingTemplates={isLoadingExportTemplates}
                  flatten={true}
                  getTemplates={getTemplates}
                />
              )}
              <Guard permissions={['procurationFees:create']} flags={['EnableProcurationFeeRulesCrud']}>
                <Table.CreateButton onClick={onCreate} />
              </Guard>
            </Space>
          </Flex>
        </Table.ActionsBar>
      </Table>
    );
  },
);
